import type { AggregationMode } from "./trendingUrlState";

export interface EnvelopeBucket {
	avg: [number, number | null];
	min: [number, number | null];
	max: [number, number | null];
}

export const reduceEnvelopeBucket = (
	data: [number, number | null][],
	startIdx: number,
	endIdx: number,
): EnvelopeBucket => {
	let sum = 0;
	let count = 0;
	let minValue = 0;
	let maxValue = 0;

	for (let i = startIdx; i <= endIdx; i++) {
		const value = data[i][1];
		if (value === null || value === undefined) {
			continue;
		}

		if (count === 0) {
			minValue = value;
			maxValue = value;
		} else {
			if (value < minValue) minValue = value;
			if (value > maxValue) maxValue = value;
		}

		sum += value;
		count++;
	}

	const timestamp = data[endIdx][0];

	if (count === 0) {
		return {
			avg: [timestamp, null],
			min: [timestamp, null],
			max: [timestamp, null],
		};
	}

	return {
		avg: [timestamp, sum / count],
		min: [timestamp, minValue],
		max: [timestamp, maxValue],
	};
};

export const reduceBucket = (
	data: [number, number | null][],
	startIdx: number,
	endIdx: number,
	mode: AggregationMode,
): [number, number | null] => {
	let firstIdx = -1;
	let lastIdx = -1;
	let minIdx = -1;
	let maxIdx = -1;
	let sum = 0;
	let count = 0;

	for (let i = startIdx; i <= endIdx; i++) {
		const value = data[i][1];
		if (value === null || value === undefined) {
			continue;
		}

		if (firstIdx === -1) {
			firstIdx = i;
		}
		lastIdx = i;
		sum += value;
		count++;

		if (minIdx === -1 || value < (data[minIdx][1] as number)) {
			minIdx = i;
		}
		if (maxIdx === -1 || value > (data[maxIdx][1] as number)) {
			maxIdx = i;
		}
	}

	if (count === 0) {
		return [data[endIdx][0], null];
	}

	switch (mode) {
		case "first":
			return [data[endIdx][0], data[firstIdx][1]];
		case "last":
			return [data[endIdx][0], data[lastIdx][1]];
		case "min":
			return [data[endIdx][0], data[minIdx][1]];
		case "max":
			return [data[endIdx][0], data[maxIdx][1]];
		default:
			return [data[endIdx][0], sum / count];
	}
};

export interface BucketOutput {
	points: [number, number | null][];
	bandMin: [number, number | null][];
	bandMax: [number, number | null][];
	realTimestamps: number[];
	firstTimestamps: number[];
}

export const closeAndEmitBucket = ({
	data,
	startIdx,
	endIdx,
	mode,
	firstTimestamp,
	into,
}: {
	data: [number, number | null][];
	startIdx: number;
	endIdx: number;
	mode: AggregationMode;
	firstTimestamp: number;
	into: BucketOutput;
}): void => {
	let point: [number, number | null];

	if (mode === "envelope") {
		const { avg, min, max } = reduceEnvelopeBucket(data, startIdx, endIdx);
		point = avg;
		into.bandMin.push(min);
		into.bandMax.push(max);
	} else {
		point = reduceBucket(data, startIdx, endIdx, mode);
	}

	into.points.push(point);
	into.firstTimestamps.push(firstTimestamp);

	if (startIdx === endIdx && point[1] !== null && point[1] !== undefined) {
		into.realTimestamps.push(point[0]);
	}
};

export const collectRealTimestamps = ({
	points,
	hasLeftAnchor,
	hasRightAnchor,
}: {
	points: [number, number | null][];
	hasLeftAnchor: boolean;
	hasRightAnchor: boolean;
}): number[] => {
	const timestamps: number[] = [];
	const lastIndex = points.length - 1;

	for (let i = 0; i <= lastIndex; i++) {
		if (hasLeftAnchor && i === 0) continue;
		if (hasRightAnchor && i === lastIndex) continue;

		const value = points[i][1];
		if (value === null || value === undefined) continue;

		timestamps.push(points[i][0]);
	}

	return timestamps;
};

export const pushAnchor = ({
	data,
	index,
	mode,
	firstTimestamp,
	into,
}: {
	data: [number, number | null][];
	index: number;
	mode: AggregationMode;
	firstTimestamp: number;
	into: BucketOutput;
}): void => {
	into.points.push(data[index]);
	into.firstTimestamps.push(firstTimestamp);

	if (mode === "envelope") {
		const [timestamp, value] = data[index];
		into.bandMin.push([timestamp, value]);
		into.bandMax.push([timestamp, value]);
	}
};

export interface BucketLayout {
	visibleStart: number;
	bucketWidth: number;
	bucketCount: number;
}

export const createBucketLayout = (
	visibleStart: number,
	visibleEnd: number,
	bucketCount: number,
): BucketLayout => {
	const span = visibleEnd - visibleStart;

	return {
		visibleStart,
		bucketCount,
		bucketWidth: span > 0 ? Math.max(1, Math.floor(span / bucketCount)) : 1,
	};
};

export const bucketIndexOf = (
	layout: BucketLayout,
	timestamp: number,
): number => {
	const index = Math.floor(
		(timestamp - layout.visibleStart) / layout.bucketWidth,
	);
	if (index < 0) return 0;
	if (index >= layout.bucketCount) return layout.bucketCount - 1;
	return index;
};

